/*  Officer Safety Report – Detail Page
    • Consistent PDF generation (shared pdfStyles)
    • Single‑column responsive layout
*/
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import {
  FiShield, FiCalendar, FiAlertTriangle, FiUser,
  FiCheckCircle, FiDownload,
} from 'react-icons/fi';
import pdfMake   from 'pdfmake/build/pdfmake';
import pdfFonts  from 'pdfmake/build/vfs_fonts';
import {
  doc, /* getDoc, */ updateDoc, Timestamp, onSnapshot // Import onSnapshot
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

import { AuthContext } from '../contexts/AuthContext';
import { db }           from '../utils/firebaseConfig';
import { pdfStyles }    from '../utils/pdfStyles';
import { parseDate, getBase64ImageFromURL } from '../utils/pdfUtils';
import { ERRORS }       from '../utils/constants';
import { createCommentNotification } from '../utils/notificationUtils';

pdfMake.vfs = pdfFonts.vfs;

/* ------------------------------------------------ PDF Constants ---- */
const PDF_CONFIG = {
  BANNER_WIDTH: 515,
  PAGE_MARGINS: [40, 20, 40, 40],
  COLORS: {
    primary: '#2563eb',      // Blue-600
    secondary: '#3b82f6',    // Blue-500
    accent: '#93c5fd',       // Blue-300
    success: '#16a34a',      // Green-600
    warning: '#d97706',      // Amber-600
    danger: '#dc2626',       // Red-600
    tableHeader: '#1e40af',  // Blue-800
    tableAlt: '#f8fafc',     // Slate-50
    text: '#1e293b',         // Slate-800
    lightText: '#64748b',    // Slate-500
    border: '#cbd5e1',       // Slate-300
    headerBg: '#dbeafe',     // Blue-100
    dangerBg: '#fee2e2',     // Red-100
    successBg: '#dcfce7',    // Green-100
  }
};

/* Enhanced PDF Styles */
const enhancedPdfStyles = {
  ...pdfStyles,
  header: {
    fontSize: 24,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 0, 0, 8],
  },
  subheader: {
    fontSize: 11,
    color: PDF_CONFIG.COLORS.text,
    margin: [0, 0, 0, 4],
  },
  sectionHeader: {
    fontSize: 14,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
    margin: [0, 15, 0, 8],
  },
  content: {
    fontSize: 10,
    color: PDF_CONFIG.COLORS.text,
    lineHeight: 1.5,
    margin: [0, 0, 0, 10],
  },
  footerText: {
    fontSize: 8,
    color: PDF_CONFIG.COLORS.lightText,
  },
  reportNumber: {
    fontSize: 10,
    color: PDF_CONFIG.COLORS.lightText,
    alignment: 'right',
  },
  bannerText: {
    fontSize: 26,
    bold: true,
    color: PDF_CONFIG.COLORS.primary,
  },
  signatureLabel: {
    fontSize: 9,
    bold: true,
    color: PDF_CONFIG.COLORS.text,
  },
  signatureDate: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.lightText,
  },
  infoBox: {
    fontSize: 9,
    color: PDF_CONFIG.COLORS.text,
    fillColor: PDF_CONFIG.COLORS.headerBg,
    margin: [8, 8],
  },
};

/* ------------------------------------------------ helpers --------- */
const toDate = d => d instanceof Timestamp ? d.toDate() : typeof d==='string' ? parseISO(d) : d;
const fmtDate = d => isValid(toDate(d)) ? format(toDate(d),'MMMM d, yyyy') : 'Unknown';
const formatPdfDate = (date, formatStr = 'MMMM d, yyyy') => {
  if (!date) return 'N/A';
  const parsed = toDate(date);
  return isValid(parsed) ? format(parsed, formatStr) : 'N/A';
};

/* ------------------------------------------------ PDF builder ----- */
export const getOSRPdfContentDefinition = async (report) => {
  if (!report) throw new Error('Invalid report data for PDF generation');
  
  const content=[];

  /* Banner with fallback */
  try{
    const banner=await getBase64ImageFromURL('/banner.png');
    content.push({ 
      image:banner, 
      width:PDF_CONFIG.BANNER_WIDTH, 
      alignment:'center', 
      margin:[0,0,0,10] 
    });
  }catch(error){
    console.warn('Failed to load banner image for PDF:', error);
    content.push({
      text: 'FIELD TRAINING PROGRAM',
      style: 'bannerText',
      alignment: 'center',
      margin: [0, 0, 0, 10]
    });
  }

  /* Report number at top right */
  content.push({
    text:`Report Number: ${report.reportId || 'N/A'}`,
    style:'reportNumber',
    alignment:'right',
    margin:[0,0,0,5],
  });

  /* Title block with enhanced styling */
  content.push(
    { text: 'Officer Safety Report', style: 'header' },
    { 
      canvas: [
        {
          type: 'line',
          x1: 0, y1: 0,
          x2: 515, y2: 0,
          lineWidth: 2,
          lineColor: PDF_CONFIG.COLORS.secondary,
        }
      ],
      margin: [0, 0, 0, 10]
    }
  );

  /* Report Information Section */
  content.push({
    table: {
      widths: ['*'],
      body: [
        [
          {
            stack: [
              {
                table: {
                  widths: ['50%', '50%'],
                  body: [
                    [
                      { 
                        text: `Date: ${formatPdfDate(report.reportDate)}`, 
                        style: 'infoBox',
                        border: [true, true, false, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                      { 
                        text: `Report Type: Officer Safety Report`, 
                        style: 'infoBox',
                        border: [false, true, true, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                    ],
                    [
                      { 
                        text: `Probationary Officer: ${report.traineeName || 'Unknown'} ${report.traineeIdNumber ? `(#${report.traineeIdNumber})` : ''}`, 
                        style: 'infoBox',
                        border: [true, false, false, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                      { 
                        text: `FTO: ${report.trainerName || 'Unknown'} ${report.trainerIdNumber ? `(#${report.trainerIdNumber})` : ''}`, 
                        style: 'infoBox',
                        border: [false, false, true, true],
                        borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
                      },
                    ]
                  ]
                },
                layout: {
                  hLineWidth: (i, node) => 1,
                  vLineWidth: (i, node) => 1,
                  hLineColor: () => PDF_CONFIG.COLORS.border,
                  vLineColor: () => PDF_CONFIG.COLORS.border,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                }
              }
            ],
            fillColor: 'white',
            border: [false, false, false, false],
          }
        ]
      ]
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 20],
  });

  /* Incident Description Section */
  content.push(
    { text:'Outline Violation / Incident Description', style:'sectionHeader' },
    {
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: report.incidentDescription || 'No description provided.',
              style: 'content',
              fillColor: 'white',
              border: [true, true, true, true],
              borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
              margin: [10, 10, 10, 10],
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => PDF_CONFIG.COLORS.border,
        vLineColor: () => PDF_CONFIG.COLORS.border,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        fillColor: () => null,
      }
    }
  );

  /* Corrective Action Section */
  content.push(
    { text:'Remedial Training / Corrective Action Taken', style:'sectionHeader' },
    {
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: report.safetyConcerns || 'No details provided.',
              style: 'content',
              fillColor: 'white',
              border: [true, true, true, true],
              borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
              margin: [10, 10, 10, 10],
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => PDF_CONFIG.COLORS.border,
        vLineColor: () => PDF_CONFIG.COLORS.border,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        fillColor: () => null,
      }
    }
  );

  /* PO Comments Section */
  content.push(
    { text:"Probationary Officer's Comments", style:'sectionHeader' },
    {
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: report.probationaryOfficerComments || 'No comments provided by the probationary officer.',
              style: 'content',
              fillColor: 'white',
              border: [true, true, true, true],
              borderColor: [PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border, PDF_CONFIG.COLORS.border],
              margin: [10, 10, 10, 10],
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => PDF_CONFIG.COLORS.border,
        vLineColor: () => PDF_CONFIG.COLORS.border,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
        fillColor: () => null,
      }
    }
  );

  /* Enhanced signature block */
  content.push({ text:'', margin:[0,0,0,40] });
  content.push({
    columns:[
      {
        width: '48%',
        stack:[
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 200, y2: 0,
                lineWidth: 1,
                lineColor: PDF_CONFIG.COLORS.text,
                dash: { length: 1 },
              }
            ],
            margin: [0, 20, 0, 5],
          },
          { 
            text:"Probationary Officer's Signature", 
            style:'signatureLabel',
            color: PDF_CONFIG.COLORS.lightText,
          },
          { 
            text:`${report.traineeName || 'Unknown'} ${report.traineeIdNumber ? `(#${report.traineeIdNumber})` : ''}`,
            fontSize: 9,
            color: PDF_CONFIG.COLORS.text,
            margin: [0, 2, 0, 5],
          },
          { 
            text:'Date: _____________________', 
            style:'signatureDate',
            color: PDF_CONFIG.COLORS.lightText,
          },
        ]
      },
      { width: '4%', text: '' },
      {
        width: '48%',
        stack:[
          {
            canvas: [
              {
                type: 'line',
                x1: 0, y1: 0,
                x2: 200, y2: 0,
                lineWidth: 1,
                lineColor: PDF_CONFIG.COLORS.text,
                dash: { length: 1 },
              }
            ],
            margin: [0, 20, 0, 5],
            alignment: 'right',
          },
          { 
            text:"Field Training Officer's Signature", 
            style:'signatureLabel', 
            alignment:'right',
            color: PDF_CONFIG.COLORS.lightText,
          },
          { 
            text:`${report.trainerName || 'Unknown'} ${report.trainerIdNumber ? `(#${report.trainerIdNumber})` : ''}`, 
            fontSize: 9,
            alignment:'right',
            color: PDF_CONFIG.COLORS.text,
            margin: [0, 2, 0, 5],
          },
          { 
            text:'Date: _____________________', 
            style:'signatureDate', 
            alignment:'right',
            color: PDF_CONFIG.COLORS.lightText,
          },
        ],
        alignment:'right'
      },
    ],
  });

  /* Footer */
  content.push({
    text: `Generated on ${format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}`,
    style: 'footerText',
    alignment: 'center',
    margin: [0, 20, 0, 0],
  });

  return content;
};

/* ------------------------------------------------ Loading Skeleton ----- */
const OSRDetailSkeleton = () => (
  <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-3 sm:p-6">
    <div className="max-w-3xl mx-auto space-y-6 animate-pulse">
      {/* PDF Button Skeleton */}
      <div className="hidden lg:flex justify-end mb-4 lg:mb-6">
        <div className="h-11 w-24 bg-secondary-light dark:bg-secondary-dark rounded-md"></div>
      </div>

      {/* Header Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="h-6 w-52 bg-secondary-light dark:bg-secondary-dark rounded mb-3"></div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-4 h-4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-4 w-28 bg-secondary-light dark:bg-secondary-dark rounded"></div>
              <div className="h-4 w-32 bg-secondary-light dark:bg-secondary-dark rounded"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Incident Description Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-64 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-3 w-4/5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-3 w-3/4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-3 w-5/6 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
      </div>

      {/* Corrective Action Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-72 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-3 w-3/4 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-3 w-4/5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
      </div>

      {/* PO Comments Card Skeleton */}
      <div className="rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 bg-secondary-light dark:bg-secondary-dark rounded"></div>
          <div className="h-6 w-56 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
        <div className="space-y-3">
          <div className="h-24 w-full bg-secondary-light dark:bg-secondary-dark rounded-lg"></div>
          <div className="h-10 w-40 bg-secondary-light dark:bg-secondary-dark rounded"></div>
        </div>
      </div>

      {/* Report ID Skeleton */}
      <div className="h-3 w-24 bg-secondary-light dark:bg-secondary-dark rounded ml-auto"></div>
    </div>
  </div>
);

/* ------------------------------------------------ React page ------ */
export default function OfficerSafetyReportDetailPage(){
  const { reportId } = useParams();
  const navigate     = useNavigate();
  const { currentUser, role } = useContext(AuthContext);

  const [report,setReport]                   = useState(null);
  const [loading,setLoading]                 = useState(true);
  const [poComments,setPOComments]           = useState('');
  const [submitting,setSubmitting]           = useState(false);

  /* fetch report */
  useEffect(()=>{
      if(!currentUser){ navigate('/login'); return; }
    
    const reportDocRef = doc(db,'officerSafetyReports',reportId);
    
    const unsubscribe = onSnapshot(reportDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setReport({
          ...data,
          id: docSnap.id, // Add id to the report object
          reportDate:parseDate(data.reportDate),
          traineeName:data.traineeName || 'Unknown',
          trainerName:data.trainerName || 'Unknown',
          traineeIdNumber:data.traineeIdNumber || '',
          trainerIdNumber:data.trainerIdNumber || '',
        });
        // Update poComments from the snapshot if it exists, otherwise keep local state
        // This allows typing in the textarea without it being overwritten by snapshots until submission
        if (data.probationaryOfficerComments !== undefined && poComments !== data.probationaryOfficerComments) {
             // Only update if there's a change from Firestore, to avoid overwriting user input
            if (document.activeElement?.name !== 'poCommentsTextarea') {
        setPOComments(data.probationaryOfficerComments || '');
            }
        }
        setLoading(false);
      } else {
        toast.error('Report not found');
        navigate('/');
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching OSR in real-time:", error);
      toast.error('Error fetching OSR data.');
      setLoading(false);
    });

    return () => unsubscribe(); // Cleanup listener
    
  },[reportId, currentUser, navigate]); // poComments removed from deps to avoid issues with textarea input

  /* submit PO comments */
  const submitComments=async()=>{
    if(!poComments.trim()){ toast.error('Please enter your comments first.'); return; }
    setSubmitting(true);
    try{
      const reportDocRef = doc(db,'officerSafetyReports',reportId);
      // Update the document. onSnapshot will handle the UI update for report.probationaryOfficerComments.
      await updateDoc(reportDocRef,{
        probationaryOfficerComments:poComments.trim(),
        probationaryOfficerCommentsTimestamp:Timestamp.now(),
      });
      // No need to call setReport here, onSnapshot will update it.
      // No need to call setPOComments here if we expect onSnapshot to update it, 
      // or clear it if that's the desired UX post-submission.
      // For now, let's assume onSnapshot handles the update, and we don't clear poComments to allow further editing if needed.
      
      // Send notification about the comment
      try {
        await createCommentNotification(
          reportId,        // reportId (Firestore doc ID)
          'OSR',           // reportType
          currentUser.uid, // commentingUserId
          currentUser.displayName || report?.traineeName || 'User', // Use current user's display name
          role,            // Use the actual user role from context
          poComments.trim() // commentText
        );
        console.log(`Attempted to send OSR comment notification from ${role} user.`);
      } catch (notifError) {
        console.error('Failed to send OSR comment notification:', notifError);
      }
      
      // toast.success('Comments submitted'); // Optional, as visual update is confirmation
    }catch(e){ 
      console.error("Error submitting OSR comments:", e); 
      toast.error('Submit failed'); 
    }
    finally{ setSubmitting(false); }
  };

  /* generate pdf */
  const generatePdf=async()=>{
    if(!report) return;
    
    const loadingToast = toast.loading('Generating PDF...');
    
    try{
      const content=await getOSRPdfContentDefinition(report);
      const docDefinition = {
        pageMargins: PDF_CONFIG.PAGE_MARGINS,
        content,
        styles: enhancedPdfStyles,
        info: {
          title: `Officer Safety Report - ${report.traineeName}`,
          author: report.trainerName,
          subject: `Officer Safety Report dated ${formatPdfDate(report.reportDate, 'yyyy-MM-dd')}`,
          keywords: 'field training, officer safety, incident report, FTO, trainee',
          creator: 'Field Training Management System',
          producer: 'Field Training Management System',
          creationDate: new Date(),
        },
        // Page settings
        pageSize: 'LETTER',
        pageOrientation: 'portrait',
        // Header for pages 2+
        header: (currentPage, pageCount) => {
          if (currentPage === 1) return null;
          return {
            columns: [
              {
                text: `Officer Safety Report - ${report.traineeName}`,
                style: 'footerText',
                alignment: 'left',
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerText',
                alignment: 'right',
              }
            ],
            margin: [40, 10, 40, 0],
          };
        },
      };
      
      pdfMake.createPdf(docDefinition)
             .download(`OfficerSafetyReport_${report.reportId || reportId}_${format(report.reportDate, 'yyyy-MM-dd')}.pdf`);
      
      toast.update(loadingToast, { 
        render: 'PDF generated successfully!', 
        type: 'success', 
        isLoading: false,
        autoClose: 3000 
      });
    }catch(e){ 
      console.error('PDF generation error:', e); 
      toast.update(loadingToast, { 
        render: ERRORS.GENERATE_PDF_FAILED || 'Failed to generate PDF', 
        type: 'error', 
        isLoading: false,
        autoClose: 5000 
      });
    }
  };

  if(loading) return <div className="flex justify-center items-center min-h-screen">Loading…</div>;
  if(!report) return <div className="flex justify-center items-center min-h-screen">Report not found</div>;

  /* tailwind snippets */
  const card   ='rounded-lg shadow-lg bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark';
  const head   ='font-semibold flex items-center gap-2 text-base sm:text-lg';
  const label  ='font-semibold text-[11px] sm:text-xs';
  const button ='min-h-[44px] px-4 py-2 rounded-md focus-visible:ring-2 focus-visible:ring-blue-500';

  return(
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-screen bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark p-3 sm:p-6"
    >
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
        className="max-w-3xl mx-auto"
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <OSRDetailSkeleton />
            </motion.div>
          ) : !report ? (
            <motion.div
              key="not-found"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center items-center min-h-screen"
            >
              <div className={`${card} p-8 text-center`}>
                <FiShield className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h2 className="text-xl font-semibold mb-2">Report Not Found</h2>
                <p className="text-gray-600 mb-4">The Officer Safety Report you're looking for doesn't exist.</p>
                <button 
                  onClick={() => navigate('/')}
                  className={`${button} bg-blue-600 hover:bg-blue-700 text-white`}
                >
                  Go Back Home
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {/* desktop‑only download */}
              {(role==='admin'||role==='trainer') && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="hidden lg:flex justify-end mb-4 lg:mb-6"
                >
                  <button onClick={generatePdf} className={`${button} bg-blue-600 hover:bg-blue-700 text-white shadow flex items-center gap-2`}>
                    <FiDownload/> PDF
                  </button>
                </motion.div>
              )}

              {/* header */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className={`${card} p-4 sm:p-6`}
              >
                <h1 className="text-lg sm:text-xl font-bold mb-3 flex items-center gap-2"><FiShield/> Officer Safety Report</h1>
                <div className="space-y-1 text-sm">
                  <p className="flex items-center gap-2"><FiCalendar/><span className={label}>Date:</span>{fmtDate(report.reportDate)}</p>
                  <p className="flex items-center gap-2"><FiUser/><span className={label}>Probationary Officer:</span>{report.traineeName} {report.traineeIdNumber}</p>
                  <p className="flex items-center gap-2"><FiUser/><span className={label}>FTO:</span>{report.trainerName} {report.trainerIdNumber}</p>
                </div>
              </motion.div>

              {/* incident description */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className={`${card} p-4 sm:p-6 mt-6`}
              >
                <h2 className={head}><FiAlertTriangle/> Outline Violation / Incident Description</h2>
                <p className="mt-3 text-xs sm:text-sm">{report.incidentDescription || 'No description provided.'}</p>
              </motion.section>

              {/* corrective action */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                className={`${card} p-4 sm:p-6 mt-6`}
              >
                <h2 className={head}><FiCheckCircle/> Remedial Training / Corrective Action Taken</h2>
                <p className="mt-3 text-xs sm:text-sm">{report.safetyConcerns || 'No details provided.'}</p>
              </motion.section>

              {/* PO comments */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7 }}
                className={`${card} p-4 sm:p-6 mt-6`}
              >
                <h2 className={head}><FiUser/> Probationary Officer's Comments</h2>

                {currentUser.uid===report.traineeId ? (
                  report.probationaryOfficerComments ? (
                    <p className="mt-3 text-xs sm:text-sm">{report.probationaryOfficerComments}</p>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.8 }}
                    >
                      <textarea
                        rows={4}
                        value={poComments}
                        onChange={e=>setPOComments(e.target.value)}
                        placeholder="Add your comments here"
                        className="mt-3 w-full p-3 rounded-lg bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark text-xs sm:text-sm focus-visible:ring-2 focus-visible:ring-blue-500"/>
                      <button
                        onClick={submitComments}
                        disabled={submitting}
                        className={`${button} mt-3 bg-blue-600 hover:bg-blue-700 text-white shadow disabled:opacity-50 flex items-center gap-2`}>
                        <FiCheckCircle/>{submitting?'Submitting…':'Submit Comments'}
                      </button>
                    </motion.div>
                  )
                ) : (
                  <p className="mt-3 text-xs sm:text-sm">{report.probationaryOfficerComments || 'No comments provided.'}</p>
                )}
              </motion.section>

              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.9 }}
                className="mt-6 text-[11px] text-right pr-20 text-text-light/60 dark:text-text-dark/60"
              >
                Report #: {report.reportId}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* mobile / tablet floating PDF */}
        {(role==='admin'||role==='trainer') && !loading && report && (
          <motion.button 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            onClick={generatePdf}
            className="lg:hidden fixed bottom-28 right-4 z-[1300] bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-xl p-4 focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <FiDownload className="w-5 h-5"/>
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
}

// Export the enhanced styles for use in compiled PDFs
export { enhancedPdfStyles };
